import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { useAuth } from "../../context/AuthContext";
import { useMemberRole } from "../../hooks/useMemberRole";
import { useFamilyMode } from "../../context/FamilyModeContext";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/firebaseConfig";
import { navigateTo } from "../../utils/navigation";
import UserAvatar from "../common/UserAvatar";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { Capacitor } from "@capacitor/core";

interface UserDropdownProps {
  hideChevronOnMobile?: boolean;
  planBadge?: string | null;
}

export default function UserDropdown({ hideChevronOnMobile = false, planBadge }: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { user } = useAuth();
  const { memberId } = useMemberRole();
  const { enabled: familyMode } = useFamilyMode();
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [avatar, setAvatar] = useState<string>("");

  const { t } = useTranslation();

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  async function handleLogout() {
    try {
      closeDropdown();
      setIsSigningOut(true);

      // Force immediate auth state resolution on native after explicit logout
      try {
        localStorage.setItem('force_signout', '1');
      } catch {
      }

      // Limpiar estado local
      localStorage.removeItem("session_start_time");
      localStorage.removeItem("firestore_cache_clear_version");
      sessionStorage.clear();

      // Cerrar sesión en Firebase
      try {
        const platform = Capacitor.getPlatform();
        if (platform === 'ios') {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          await FirebaseAuthentication.signOut();
        }
      } catch {
      }
      await signOut(auth);

      // Recargar página para limpiar estado
      navigateTo("/signin");
    } catch (error) {
    } finally {
      setIsSigningOut(false);
    }
  }

  useEffect(() => {
    setName("");
    setEmail(user?.email || "");
    setAvatar("");

    if (!user?.uid) return;

    let unsub: (() => void) | null = null;
    let unsubMember: (() => void) | null = null;
    let unsubActiveMember: (() => void) | null = null;

    // ✅ FIX: Prioridad correcta de nombres:
    // 1. memberId vinculado al usuario (miembro de la familia)
    // 2. Nombre del user document
    // 3. active_member_id solo como fallback

    const activeMemberId = localStorage.getItem('active_member_id');

    const timer = setTimeout(() => {
      unsub = onSnapshot(
        doc(db, "users", user.uid),
        (snap) => {
          const data = snap.exists() ? (snap.data() as any) : null;
          const userName = (data?.name as string | undefined) || "";
          const userAvatar = data?.avatar || "";

          // Solo usar datos de usuario si NO hay memberId vinculado
          // (memberId tiene prioridad porque es el miembro asociado al user en esta familia)
          if (!memberId) {
            if (userName) setName(userName);
            setAvatar(userAvatar);
          }
          setEmail(data?.email || user.email || "");
        },
        (error) => {
        }
      );
    }, 1000);

    // ✅ PRIORIDAD 1: memberId vinculado al usuario en la familia actual
    if (memberId) {
      unsubMember = onSnapshot(
        doc(db, "members", memberId),
        (snap) => {
          const data = snap.exists() ? (snap.data() as any) : null;
          const memberName = (data?.name as string | undefined) || "";
          const memberAvatar =
            (data?.avatar as string | undefined) ||
            (data?.photoURL as string | undefined) ||
            "";

          if (memberName) {
            setName(memberName);
          }
          if (memberAvatar) {
            setAvatar(memberAvatar);
          }
        },
        () => {}
      );
    }
    // ✅ PRIORIDAD 3: active_member_id como fallback (solo si no hay memberId)
    else if (activeMemberId) {
      unsubActiveMember = onSnapshot(
        doc(db, "members", activeMemberId),
        (snap) => {
          const data = snap.exists() ? (snap.data() as any) : null;
          const activeName = (data?.name as string | undefined) || "";
          const activeAvatar =
            (data?.avatar as string | undefined) ||
            (data?.photoURL as string | undefined) ||
            "";
          // Solo usar si no hay memberId (ya que memberId tiene prioridad)
          if (!memberId) {
            if (activeName) setName(activeName);
            if (activeAvatar) setAvatar(activeAvatar);
          }
        },
        () => {}
      );
    }

    return () => {
      clearTimeout(timer);
      if (unsub) unsub();
      if (unsubMember) unsubMember();
      if (unsubActiveMember) unsubActiveMember();
    };
  }, [user?.uid, user?.email, memberId]);

  return (
    <div className="relative">
      {isSigningOut && (
        <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-[24px] p-6 shadow-2xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-3"></div>
              <p className="text-gray-700 dark:text-gray-300">
                Cerrando sesión...
              </p>
            </div>
          </div>
        </div>
      )}
      <button
        onClick={toggleDropdown}
        className={`flex items-center ${hideChevronOnMobile ? "gap-2" : "gap-3"} rounded-full hover:bg-white/20 dark:hover:bg-white/10 p-2 transition-colors`}
      >
        <UserAvatar
          name={name || email || "User"}
          avatarUrl={avatar || null}
          size="sm"
        />
        <span className="hidden sm:block font-black text-theme-sm max-w-[140px] truncate text-[#055590]">
          {name || email || 'profile' /* TODO: translate inline */}
        </span>
        <svg
          className={`stroke-[#055590] dark:stroke-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""} ${hideChevronOnMobile ? "hidden sm:block" : ""}`}
          width="18"
          height="20"
          viewBox="0 0 18 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4.3125 8.65625L9 13.3437L13.6875 8.65625" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute right-0 mt-[17px] flex w-[260px] flex-col rounded-[24px] bg-white/60 dark:bg-gray-900/50 backdrop-blur-sm p-3 shadow-theme-lg"
      >
        <div>
          <span className="block font-black text-gray-700 text-theme-sm dark:text-gray-400">
            {name || 'profile' /* TODO: translate inline */}
          </span>
          <span className="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
            {email}
          </span>
          {planBadge && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-black rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-blue-600 dark:text-blue-400">
                <rect x="3.75" y="5.25" width="16.5" height="13.5" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                <path d="M3.75 9.75h16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M8.25 13.5h7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              <span>{planBadge}</span>
            </div>
          )}
        </div>
        {!familyMode && (
          <ul className="flex flex-col gap-1 pt-4 pb-3 border-b border-gray-200 dark:border-gray-800">
            <li>
              <DropdownItem
                onItemClick={closeDropdown}
                tag="a"
                to="/profile"
                className="flex items-center gap-3 px-3 py-2 font-black text-gray-700 rounded-2xl group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-500 dark:text-gray-400">
                  <path d="M15.75 7.5a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4.5 20.25a7.5 7.5 0 1 1 15 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t("header.profile")}
              </DropdownItem>
            </li>

            <li>
              <DropdownItem
                onItemClick={closeDropdown}
                tag="a"
                to="/subscription"
                className="flex items-center gap-3 px-3 py-2 font-black text-gray-700 rounded-2xl group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-500 dark:text-gray-400">
                  <rect x="3.75" y="5.25" width="16.5" height="13.5" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M3.75 9.75h16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  <path d="M8.25 13.5h7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                {t("nav.subscription")}
              </DropdownItem>
            </li>

            <li>
              <DropdownItem
                onItemClick={closeDropdown}
                tag="a"
                to="/referrals"
                className="flex items-center gap-3 px-3 py-2 font-black text-gray-700 rounded-2xl group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-500 dark:text-gray-400">
                  <path d="M12 2.25l2.955 5.987 6.607.96-4.781 4.66 1.129 6.582L12 17.25l-5.91 3.189 1.129-6.582-4.781-4.66 6.607-.96L12 2.25Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                </svg>
                {t("nav.referrals")}
              </DropdownItem>
            </li>

            <li>
              <DropdownItem
                onItemClick={closeDropdown}
                tag="a"
                to="/preferences"
                className="flex items-center gap-3 px-3 py-2 font-black text-gray-700 rounded-2xl group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-500 dark:text-gray-400">
                  <path d="M12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19.5 12a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2.25v2.25M12 19.5v2.25M2.25 12h2.25M19.5 12h2.25M4.22 4.22l1.59 1.59M18.19 18.19l1.59 1.59M4.22 19.78l1.59-1.59M18.19 5.81l1.59-1.59" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                {t("nav.preferences")}
              </DropdownItem>
            </li>

            <li>
              <DropdownItem
                onItemClick={closeDropdown}
                tag="a"
                to="/settings/personalization"
                className="flex items-center gap-3 px-3 py-2 font-black text-gray-700 rounded-2xl group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-500 dark:text-gray-400">
                  <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 6h9.75m-9.75 0a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0m-3.75 0H7.5m3 6h9.75m-9.75 0a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0m-3.75 0H7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t("nav.personalization")}
              </DropdownItem>
            </li>

          </ul>
        )}

        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 mt-3 font-black text-gray-700 rounded-2xl group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 w-full text-left"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-500 dark:text-gray-400">
            <path d="M15.75 9V4.5A2.25 2.25 0 0 0 13.5 2.25h-9A2.25 2.25 0 0 0 2.25 4.5v15A2.25 2.25 0 0 0 4.5 21.75h9a2.25 2.25 0 0 0 2.25-2.25V15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 12h9m0 0-3-3m3 3-3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t("header.logout")}
        </button>
      </Dropdown>
    </div>
  );
}
